import { Box, Slab } from '../../render/parts'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Freestanding acoustic screen: an upholstered field recessed inside a slim
 * aluminium frame, carried by two low feet.
 *
 * Panels of this family are meant to be chained end to end, so the declared
 * footprint is exact (1.2 wide) and every part — feet included — stays inside
 * it. Nothing overhangs, which lets two units butt together cleanly on the grid.
 */

const WIDTH = 1.2
/** Frame depth. The declared footprint is deeper to cover the feet. */
const FRAME_DEPTH = 0.058
/** Height of the top and bottom rails. */
const RAIL = 0.032
/** Underside of the frame — the feet occupy everything below it. */
const FRAME_BASE = 0.06

/**
 * One foot: a flat plate on the floor and a short riser into the bottom rail.
 * Both stay within the 0.08 footprint depth so chained panels stay flush.
 */
function ScreenFoot({ x }: { x: number }) {
  return (
    <group position={[x, 0, 0]}>
      <Slab tone="metalDark" size={[0.26, 0.07, 0.022]} radius={0.008} position={[0, 0.011, 0]} />
      {/* riser overlaps both the plate and the rail so no faces are coplanar */}
      <Box tone="metalDark" size={[0.055, 0.06, 0.05]} position={[0, 0.044, 0]} />
    </group>
  )
}

function PartitionPanel({ accent, variant = 'h160' }: AssetContext) {
  const height = variant === 'h120' ? 1.2 : 1.6
  const faced = variant === 'accent'

  // Rails run the full width and define the silhouette; the stiles are tucked
  // 3 mm inside them and the upholstery 7 mm behind, so nothing shares a plane.
  const stileHeight = height - 0.112
  const stileY = (height + 0.06) / 2
  const coreHeight = height - 0.078
  const coreY = (height + 0.062) / 2

  return (
    <group>
      <ScreenFoot x={-(WIDTH / 2 - 0.16)} />
      <ScreenFoot x={WIDTH / 2 - 0.16} />

      {/* bottom and top rails */}
      <Box tone="metal" size={[WIDTH, RAIL, FRAME_DEPTH]} position={[0, FRAME_BASE + RAIL / 2, 0]} />
      <Box tone="metal" size={[WIDTH, RAIL, FRAME_DEPTH]} position={[0, height - RAIL / 2, 0]} />

      {/* side stiles */}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="metal"
          size={[0.03, stileHeight, 0.05]}
          position={[side * (WIDTH / 2 - 0.018), stileY, 0]}
        />
      ))}

      {/* upholstered acoustic core, recessed inside the frame */}
      <Box
        tone={faced ? 'accent' : 'shellSoft'}
        color={faced ? accent : undefined}
        size={[WIDTH - 0.024, coreHeight, 0.044]}
        position={[0, coreY, 0]}
      />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'partition-panel',
  name: 'Panel divisor',
  category: 'partitions',
  footprint: [WIDTH, 0.08],
  height: 1.6,
  accentable: true,
  variants: [
    { id: 'h160', name: '1.6 m' },
    { id: 'h120', name: '1.2 m' },
    { id: 'accent', name: 'Acento' },
  ],
  keywords: [
    'panel',
    'divisor',
    'mampara',
    'biombo',
    'acustico',
    'partition',
    'screen',
    'divider',
    'acoustic',
  ],
  Component: PartitionPanel,
}
